import { logger } from 'module/logger';
import { HttpClient } from 'api/http-client';
import { API_HOST_AGENTS } from 'config/environment';

export interface StreamAuthTokenResponse {
  token: string;
  expiryMinutes: number;
}

export interface DocumentAgentStreamData {
  id: string;
  timestamp: string;
  useCase: string;
  step: string;
  status: string;
  last_id: string;
  data: {
    nodeName?: string;
    description?: string;
    progress?: number;
  };
}

/** Rows from `GET .../real-time/{channel}/filter?status=RUNNING&step=HTML_FORMATTING`. */
export type HtmlFormattingFilterRow = {
  redis_message_id?: string;
  timestamp?: string;
  use_case?: string;
  step?: string;
  status?: string;
  data?: {
    promptId?: number;
    token?: string;
  };
  metadata?: Record<string, unknown>;
};

/** SSE payloads from `/real-time/stream/{channel}/secure?status=COMPLETED` (page generation thinking). */
export type PageGenerationThinkingStreamEvent = {
  redis_message_id?: string;
  timestamp?: string;
  use_case?: string;
  step: string;
  status: string;
  data?: {
    description?: string;
    message?: string;
    nodeName?: string;
    [key: string]: unknown;
  };
  metadata?: Record<string, unknown>;
};

class StreamAPIClient extends HttpClient {
  public getDocumentAgentStreamAuthToken = (
    documentId: number,
  ): Promise<StreamAuthTokenResponse> => {
    return this.post('/streaming/token', {
      useCase: 'DOCUMENT_AGENT_RUN',
      subjectFields: {
        documentId,
      },
    });
  };
  public streamFinalContentForMeetingPage = ({
    token,
    channelName,
    identifierValue = '',
    onContentReceived,
    onStreamingComplete,
    onPostProcessing,
    onStart,
  }: {
    token: any;
    channelName: string;
    identifierValue?: string;
    onContentReceived: (content: any) => void;
    onStreamingComplete?: () => void;
    onPostProcessing?: (content: any) => void;
    onStart?: () => void;
  }): Promise<Response | undefined> => {
    const apiKey = JSON.parse(localStorage.getItem('__api_key') ?? '[]')?.[1];
    const authorizationToken = `Bearer ${token}`;

    return this.getStream(
      `${API_HOST_AGENTS}/real-time/stream/${channelName}/secure${
        identifierValue != null && identifierValue !== ''
          ? `?data.promptId=${identifierValue}`
          : ''
      }`,
      {
        headers: {
          'api-key': apiKey ?? '',
          'Content-Type': 'application/json',
          Authorization: authorizationToken,
        },
        onStreamingComplete: () => {
          onStreamingComplete?.();
        },
        onChunkReceived: (chunk) => {
          // Split chunk into lines (handles multiple events in one chunk)
          const lines = chunk.split('\n');

          for (const line of lines) {
            const cleaned = line.replace(/^data:\s*/g, '').trim();

            if (!cleaned) {
              continue;
            } // skip empty lines

            try {
              const obj = JSON.parse(cleaned);

              switch (obj.step) {
                case 'HTML_FORMATTING':
                  if (obj.status === 'INIT') {
                    onStart?.();
                  } else if (obj.data.token) {
                    onContentReceived?.(obj.data.token);
                  }
                  break;
                case 'SEMANTIC_FORMATTING':
                  if (obj.status === 'INIT') {
                    onStart?.();
                  } else if (obj.data.token) {
                    onContentReceived?.(obj.data.token);
                  }
                  break;
                case 'SECTION_PROCESSED':
                  onPostProcessing?.(obj.data.finalContent.content);
                  break;
                case 'CONTEXT_FETCHER':
                  onContentReceived?.(obj.data);
                  break;
                case 'COMPLETED':
                  onStreamingComplete?.();
                  break;
              }
            } catch (e) {
              logger.error('could not parse stream chunk', { error: e });
              // Ignore lines that are not valid JSON
              continue;
            }
          }
        },
      },
    );
  };

  /**
   * Buffered HTML_FORMATTING RUNNING token events for replay (meeting page preview).
   */
  public getRunningHtmlFormattingTokens = ({
    token,
    channelName,
  }: {
    token: string;
    channelName: string;
  }): Promise<HtmlFormattingFilterRow[]> => {
    const apiKey = JSON.parse(localStorage.getItem('__api_key') ?? '[]')?.[1];
    const authorizationToken = `Bearer ${token}`;

    return this.get<unknown>(
      `${API_HOST_AGENTS}/real-time/${encodeURIComponent(
        channelName,
      )}/filter?status=RUNNING&step=HTML_FORMATTING`,
      {
        headers: {
          'api-key': apiKey ?? '',
          'Content-Type': 'application/json',
          Authorization: authorizationToken,
        },
      },
    ).then((res) =>
      Array.isArray(res) ? (res as HtmlFormattingFilterRow[]) : [],
    );
  };

  public getPostProcessingContent = ({
    token,
    channelName,
    useCase = 'PAGE_GENERATION_AGENT',
    step = 'PAGE_PROCESSED',
  }: {
    token: string;
    channelName: string;
    useCase?: string;
    step?: string;
  }) => {
    const apiKey = JSON.parse(localStorage.getItem('__api_key') ?? '[]')?.[1];
    const authorizationToken = `Bearer ${token}`;

    return this.get(
      `${API_HOST_AGENTS}/real-time/${encodeURIComponent(
        channelName,
      )}/filter?status=COMPLETED&useCase=${useCase}&step=${step}`,
      {
        headers: {
          'api-key': apiKey ?? '',
          'Content-Type': 'application/json',
          Authorization: authorizationToken,
        },
      },
    ).then((res: any) => {
      return res;
    });
  };

  /**
   * Subscribes to completed thinking steps for page generation. Resolves when
   * `step === "END"` and `status === "COMPLETED"`. Does not surface `END` or
   * `PAGE_PROCESSED` events to `onThinkingStep`.
   */
  public streamPageGenerationThinkingLogs = async ({
    token,
    channelName,
    onThinkingStep,
    signal,
  }: {
    token: string;
    channelName: string;
    onThinkingStep?: (event: PageGenerationThinkingStreamEvent) => void;
    signal?: AbortSignal;
  }): Promise<void> => {
    const apiKey = JSON.parse(localStorage.getItem('__api_key') ?? '[]')?.[1];
    const authorizationToken = `Bearer ${token}`;
    const url = `${API_HOST_AGENTS}/real-time/stream/${encodeURIComponent(
      channelName,
    )}/secure?status=COMPLETED`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'api-key': apiKey ?? '',
        'Content-Type': 'application/json',
        Authorization: authorizationToken,
        Accept: '*/*',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`Thinking stream failed: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Thinking stream has no response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const handleLine = (line: string) => {
      const cleaned = line.replace(/^data:\s*/g, '').trim();

      if (!cleaned) {
        return;
      }

      try {
        const obj = JSON.parse(cleaned) as PageGenerationThinkingStreamEvent;

        if (obj.step === 'END' && obj.status === 'COMPLETED') {
          return 'done' as const;
        }

        if (obj.step === 'PAGE_PROCESSED') {
          return;
        }

        const description =
          typeof obj.data?.description === 'string'
            ? obj.data.description.trim()
            : '';

        if (description) {
          onThinkingStep?.(obj);
        }
      } catch (e) {
        logger.error('could not parse thinking stream chunk', { error: e });
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const result = handleLine(line);

          if (result === 'done') {
            return;
          }
        }
      }

      if (buffer.trim()) {
        const result = handleLine(buffer);

        if (result === 'done') {
          return;
        }
      }
    } finally {
      reader.releaseLock();
    }
  };

  public streamDocumentAgentProgress = ({
    token,
    channelName,
    onProgressUpdate,
    onStreamingComplete,
  }: {
    token: string;
    channelName: string;
    onProgressUpdate: (data: DocumentAgentStreamData) => void;
    onStreamingComplete?: () => void;
  }): Promise<Response | undefined> => {
    const apiKey = JSON.parse(localStorage.getItem('__api_key') ?? '[]')?.[1];
    const authorizationToken = `Bearer ${token}`;

    return this.getStream(
      `${API_HOST_AGENTS}/real-time/stream/${channelName}/secure`,
      {
        headers: {
          'api-key': apiKey ?? '',
          'Content-Type': 'application/json',
          Authorization: authorizationToken,
        },
        onStreamingComplete: () => {
          onStreamingComplete?.();
        },
        onChunkReceived: (chunk) => {
          const lines = chunk.split('\n');

          for (const line of lines) {
            const cleaned = line.replace(/^data:\s*/g, '').trim();

            if (!cleaned) {
              continue;
            }

            try {
              const obj: DocumentAgentStreamData = JSON.parse(cleaned);

              onProgressUpdate(obj);

              if (obj.status === 'COMPLETED' && obj.step === 'COMPLETED') {
                onStreamingComplete?.();
              }
            } catch (e) {
              logger.error('could not parse stream chunk', { error: e });
              continue;
            }
          }
        },
      },
    );
  };
}

export const StreamAPI = new StreamAPIClient();
